// -----------------------------------------------------------------------------
// A6 Project setup helpers (Part 4) — pure functions, unit-tested in tests/unit/setup.test.ts.
// The database checks every rule again (0010_setup_admins.sql); these only shape the page.
// -----------------------------------------------------------------------------
import type { SetupComponent } from "../../core/api/types";

export const MAX_DISCIPLINES = 7;
export const MAX_SIGNATORIES = 8;
/** A6.1: the 10 colours offered for a new discipline (the 6 default colours are reserved). */
export const DISCIPLINE_COLOURS = ["#639922", "#BA7517", "#A32D2D", "#5F5E5A", "#3C3489", "#72243E", "#97C459", "#85B7EB", "#0C447C", "#5DCAA5"];
export const PREFIX_RE = /^[A-Z][A-Z0-9]{1,3}$/;

/** Problems in the components list before Save (names, prefix format, prefixes used twice). */
export function componentProblems(rows: { name: string; prefix: string }[]): string[] {
  const out: string[] = [];
  if (rows.some((r) => !r.name.trim())) out.push("Every component needs a name");
  for (const r of rows) {
    const p = r.prefix.trim().toUpperCase();
    if (!PREFIX_RE.test(p)) out.push(`Prefix "${p}" must be 2–4 capital letters or digits, starting with a letter (e.g. GF)`);
  }
  const seen = new Set<string>();
  for (const r of rows) {
    const p = r.prefix.trim().toUpperCase();
    if (p && seen.has(p)) out.push(`Prefix ${p} is used twice`);
    seen.add(p);
  }
  return [...new Set(out)];
}

/** Prefix changes that re-code existing issues (A6.2 asks first). */
export function prefixRenames(before: SetupComponent[], after: { id: string | null; prefix: string }[]): { from: string; to: string; issues: number }[] {
  return after.flatMap((r) => {
    const was = before.find((c) => c.id && c.id === r.id);
    const to = r.prefix.trim().toUpperCase();
    return was && was.issues > 0 && was.prefix !== to ? [{ from: was.prefix, to, issues: was.issues }] : [];
  });
}

/**
 * Signing page check before Save (round 18 #3, bug v040-010). Rows are numbered from 1.
 * A row with anything typed needs a name; a date that can't be read stops Save. A completely empty row is ignored.
 */
export function signatoryProblems(rows: { name: string; title: string; company: string; sign_date: string | null }[], badDates: Set<number>): { row: number; text: string }[] {
  const out: { row: number; text: string }[] = [];
  rows.forEach((r, i) => {
    const typed = !!(r.name.trim() || r.title.trim() || r.company.trim() || r.sign_date) || badDates.has(i);
    if (typed && !r.name.trim()) out.push({ row: i + 1, text: "needs a name" });
    if (badDates.has(i)) out.push({ row: i + 1, text: "date must look like 05/Mar/26" });
  });
  return out;
}

/**
 * Components after a refresh (round 18 #7): rows the user hasn't edited follow the database; an edited name or prefix
 * is kept. If the list is unchanged (or already matches the new data, e.g. right after Save) it is simply replaced.
 */
export function mergeRows<R extends { id: string | null; name: string; prefix: string; issues: number }>(
  cur: R[], before: SetupComponent[], after: SetupComponent[], fresh: (cs: SetupComponent[]) => R[],
): R[] {
  const shape = (xs: { name: string; prefix: string }[]) => JSON.stringify(xs.map((x) => [x.name.trim(), x.prefix.trim().toUpperCase()]));
  if (shape(cur) === shape(before) || shape(cur) === shape(after)) return fresh(after);
  return cur.map((r) => {
    const was = before.find((c) => c.id && c.id === r.id), now = after.find((c) => c.id && c.id === r.id);
    if (!was || !now) return r;
    return { ...r, name: r.name === was.name ? now.name : r.name, prefix: r.prefix === was.prefix ? now.prefix : r.prefix, issues: now.issues };
  });
}

/** Read an image file and shrink it (longest side ≤ max px) to a data URL small enough to store (≤ 1.4 MB of text). */
export async function readImage(file: File, max: number): Promise<string> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error("Pick a PNG, JPG or WebP picture");
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => bad(new Error("That picture could not be read")); i.src = url; });
    const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.naturalWidth * k)); c.height = Math.max(1, Math.round(img.naturalHeight * k));
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    let out = c.toDataURL(file.type === "image/jpeg" ? "image/jpeg" : "image/png", 0.85);
    if (out.length > 1_400_000) out = c.toDataURL("image/jpeg", 0.75);
    if (out.length > 1_400_000) throw new Error("That picture is too large — use one under about 1 MB");
    return out;
  } finally { URL.revokeObjectURL(url); }
}
